import { SignRequest, TransferComponentProps } from '@bitte-ai/types';
import { useTokenInfo } from '../../../hooks/useTokenInfo';
import { getTokenDisplayName, shortenString } from '../../../lib/utils';
import { useAccount } from '../../../state/wallet.state';
import { formatUsdValue } from '../../../utils/numberFormatting';
import NetworkBadge from './NetworkBadge';
import { TxButtons } from './TxButtons';
interface TransferComponentPropsWithTx extends TransferComponentProps {
  tx?: SignRequest;
}

export default function Transfer({ data, tx }: TransferComponentPropsWithTx) {
  const { evmAddress, chainId } = useAccount();

  const { network, receiver } = data;

  const {
    data: enrichedData,
    loading,
    balance,
  } = useTokenInfo(
    data,
    tx?.chainId.toString() || chainId?.toString() || '',
    'transfer-ft',
    evmAddress,
  );

  const displayData = enrichedData || data;
  const { token: enrichedToken } = displayData;

  const tokenName = getTokenDisplayName(enrichedToken);

  return (
    <div className="bitte-w-full bitte-max-w-md bitte-h-auto bitte-max-h-full bitte-flex bitte-flex-col bitte-gap-3 bitte-mx-auto bitte-bg-black/80 bitte-rounded-lg bitte-p-4 bitte-border bitte-border-gray-800 bitte-animate-in bitte-fade-in-0 bitte-slide-in-from-top-4 bitte-duration-300">
      <div className="bitte-flex">
        <NetworkBadge network={network} iconOverride={network?.icon} />
      </div>

      <div className="bitte-px-4 bitte-py-3 bitte-bg-[#1a1a1a] bitte-rounded bitte-flex-shrink-0 bitte-transition-all bitte-duration-200">
        <div className="bitte-text-[#5D5A5A] bitte-text-[10px] bitte-mb-1 bitte-sm:bitte-mb-2 bitte-uppercase">
          Recipient
        </div>
        <div className="bitte-flex bitte-justify-between bitte-items-center">
          <div>
            <div className="bitte-text-white bitte-text-2xl bitte-font-medium bitte-transition-all bitte-duration-200">
              {shortenString(receiver, 8)}
            </div>
          </div>
        </div>
      </div>

      <div className="bitte-px-4 bitte-py-3 bitte-bg-[#1a1a1a] bitte-rounded bitte-flex-shrink-0 bitte-transition-all bitte-duration-200">
        <div className="bitte-text-[#5D5A5A] text-xs mb-1 sm:mb-2 uppercase">
          Asset
        </div>
        <div className="bitte-flex bitte-justify-between bitte-items-center">
          <div>
            <div className="bitte-flex bitte-items-center bitte-gap-2 bitte-sm:bitte-gap-3 bitte-items-center">
              <div>
                {loading && !enrichedToken?.icon ? (
                  <div className="bitte-h-6 bitte-w-6 bitte-bg-gray-600 bitte-rounded-full bitte-transition-all bitte-duration-200" />
                ) : typeof enrichedToken?.icon === 'string' ? (
                  <img
                    src={enrichedToken?.icon}
                    alt={tokenName}
                    width={24}
                    height={24}
                    className="rounded-full"
                  />
                ) : (
                  <div className="bitte-h-6 bitte-w-6 bitte-bg-green-500 bitte-rounded-full text-[6px] bitte-flex bitte-items-center bitte-justify-center bitte-transition-all bitte-duration-200">
                    {enrichedToken?.symbol?.slice(0, 2)}{' '}
                  </div>
                )}
              </div>
              <span className="bitte-text-white bitte-text-sm bitte-font-semibold bitte-transition-all bitte-duration-200">
                {tokenName}
              </span>
            </div>
            <div className="bitte-mt-1 bitte-transition-all bitte-duration-200">
              <span className="bitte-text-[#858585] bitte-text-sm">
                Balance: {balance || '0'}
              </span>
            </div>
          </div>
          <div>
            <div className="bitte-text-white bitte-text-2xl bitte-font-medium bitte-transition-all bitte-duration-200">
              {enrichedToken?.amount || '0'}
            </div>
            <div className="bitte-flex bitte-items-center bitte-gap-1 bitte-sm:bitte-gap-2 bitte-mt-1">
              <span className="bitte-text-[#858585] bitte-text-sm bitte-transition-all bitte-duration-200">
                {loading && !enrichedToken?.usdValue ? (
                  <span className="bitte-text-[#666] bitte-text-xs bitte-animate-pulse">
                    Loading price...
                  </span>
                ) : enrichedToken?.usdValue ? (
                  <span>~ {formatUsdValue(enrichedToken?.usdValue)}</span>
                ) : null}
              </span>
            </div>
          </div>
        </div>
      </div>

      <div className="bitte-transition-all bitte-duration-200">
        <TxButtons tx={tx} />
      </div>
    </div>
  );
}
